/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInjectReducer } from 'redux-injectors';

import { PDPWidget } from 'apps/pixlee/desktop';
import { PDPWidgetMobile } from 'apps/pixlee/mobile';
import { reducer as productQuestionsAndAnswers } from 'features/productQuestionsAndAnswers';
import { resetState as questionsResetState } from 'features/productQuestionsAndAnswers/slice';
import { reviewsSearchInstanceReducer } from 'features/productSearchUgc';
import { showMoreResults } from 'features/productSearchUgc/reviewsInstanceSlice';
import ProductReviewsSearch from 'features/productSearchUgc/components/ProductReviewsSearch';
import ProductSearchUGCResults from 'features/productSearchUgc/components/ProductSearchUGCResults';
import { utagLinkPDP } from 'features/tealium';
import { Button } from 'components/Button';
import { usePrevious } from 'utilities/react';

import injectTurnToSpeedFlex from '../injectTurnToSpeedFlex';
import productReviews, { fetchProductReviews, removeFilter, resetState as reviewsResetState } from '../reducers';
import initProductReviewFormTracking from '../analytics/initProductReviewFormTracking';

import ProductReviewFilter from './ProductReviewFilter';
import ProductReviewRatingGraph from './ProductReviewRatingGraph';
import ProductReviewsList from './ProductReviewsList';
import ProductReviewSort from './ProductReviewSort';
import ProductReviewRatingStars from './ProductReviewRatingStars';

import './ProductReviews.scss';

const { globals } = window.lp;
const pixleeWidgetShowNextPrevButtonPDPPageToggleOn = JSON.parse(
	window.lp.globals.pixleeWidgetShowNextPrevButtonPDPPageToggleOn
);

const initialSortOptions = [
	{
		value: 1,
		text: 'Most Helpful'
	},
	{
		value: 2,
		text: 'Most Recent'
	},
	{
		value: 3,
		text: 'Highest to Lowest'
	},
	{
		value: 4,
		text: 'Lowest to Highest'
	}
];

const relativeUrl = window.location.pathname + window.location.search;
const gaEvents = {
	click: {
		eventCategory: 'PDP',
		eventAction: 'Click-Customer-Photos',
		eventLabel: (item, index) => `${index + 1}-${relativeUrl}`
	},
	load: {
		eventCategory: 'PDP',
		eventAction: 'Load-Customer-Photos',
		eventLabel: () => relativeUrl
	}
};

function ProductReviews ({
	resetInitialState = false, productData, reviewsData, reviewsSummaryData, questionsData, isMobile
}) {

	useInjectReducer({ key: 'productReviews', reducer: productReviews });
	// For ProductSearchUGCResults
	useInjectReducer({ key: 'productSearchUgc_Reviews', reducer: reviewsSearchInstanceReducer });
	// For ProductSearchUGCResults
	useInjectReducer({ key: 'productQuestionsAndAnswers', reducer: productQuestionsAndAnswers });

	const {
		reviews,
		reviewFilters,
		reviewsSummary,
		sortOrder,
		pageNumber,
		previousPageNumber,
		fetchStatus: reviewsFetchStatus
	} = useSelector((state) => state.productReviews);
	const activeSortOption = initialSortOptions.find((s) => s.value === sortOrder);
	const activeSortText = activeSortOption ? activeSortOption.text : initialSortOptions[0].text;

	const { fetchStatus: questionsAndAnswersFetchStatus } = useSelector((state) => state.productQuestionsAndAnswers);

	const {
		searchResults,
		fetchStatus: searchFetchStatus,
		showMoreResultsFectchStatus,
		totalResultsCount,
		totalResultsLeft,
		searchTerm
	} = useSelector((state) => state.productSearchUgc_Reviews);

	const dispatch = useDispatch();
	const [ customerPhotosState, setCustomerPhotosState ] = useState('loading');
	const [ didInitialDispatch, setDidInitialDispatch ] = useState(false);
	const showRatingBreakdown = reviewsSummary && reviewsSummary.ratings > 0;
	const numOfReviews = reviewsSummary?.reviews || 0;
	const prevReviewFilters = usePrevious(reviewFilters);
	const prevSortOrder = usePrevious(sortOrder);

	function handleRemoveFilter (filter) {

		dispatch(removeFilter(filter));
		utagLinkPDP('TurnTo')({
			eventAction: 'Remove-Ratings-Filter',
			eventLabel: filter.label
		});

	}

	useEffect(() => {

		// First load, avoid making extra call to API on mount. We get initial data on page load and dispatch it
		// Order dependency: Load Q&A then proceed to check to load reviews, condition may return early
		if (questionsAndAnswersFetchStatus === '') {

			dispatch(questionsResetState({ questionsData }));
			// continue; ??

		}
		if (reviewsFetchStatus === '') {

			dispatch(reviewsResetState({ reviewsData, reviewsSummaryData }));
			return;

		}
		// After initial dispatch reviwsFetchStatus condition skipped
		if (!didInitialDispatch) {

			setDidInitialDispatch(true);
			return;

		}

		const fetchParameters = {
			pageNumber: pageNumber > previousPageNumber ? pageNumber : 1,
			pageSize: pageNumber > previousPageNumber ? 10 : 5,
			sortOrder,
			filters: reviewFilters
		};

		if (prevReviewFilters !== reviewFilters || prevSortOrder !== sortOrder) {

			fetchParameters.pageNumber = 1;

		}

		dispatch(fetchProductReviews(fetchParameters));

	}, [ pageNumber, sortOrder, reviewFilters ]);

	// Reset
	useEffect(() => {

		if (resetInitialState && reviewsFetchStatus === 'fulfilled') {

			dispatch(reviewsResetState({ reviewsData, reviewsSummaryData }));

		}

	}, [ resetInitialState ]);

	const handleClickShowMore = () => {

		dispatch(showMoreResults());

	};

	return (
		<div className="productReviews">
			<div
				className={classNames({
					productReviews__header: true,
					'productReviews__header--noreviews': numOfReviews === 0
				})}
			>
				<div>
					{numOfReviews > 0 ? (
						<div className="productReviews__aggregateWidget">
							<ProductReviewRatingStars
								rating={reviewsSummary.averageRating}
								size="large"
								showSummaryText
							/>
						</div>
					) : (
						<p className="productReviews__emptyMessage">Be the first to review this item.</p>
					)}
					<div className="productReviews__writeReviewBtnWrapper">
						<Button
							type="button"
							buttonStyleVariant="secondary"
							className={classNames('productReviews__writeReviewBtn', {
								noReviews: numOfReviews === 0
							})}
							onClick={() => {

								injectTurnToSpeedFlex();
								utagLinkPDP('TurnTo')({
									eventAction: 'Click-WriteAReview',
									eventLabel: `${window.shortSku}`
								});
								window.TurnToCmd('reviewsList.writeReview');
								initProductReviewFormTracking();

							}}
						>
							Write a review
						</Button>
					</div>
				</div>
				{/* Rating Breakdown Graph */}
				{showRatingBreakdown && (
					<div>
						<ProductReviewRatingGraph reviewsSummary={reviewsSummary} />
					</div>
				)}
			</div>

			{/* Filters (mobile) */}
			{reviewFilters.length > 0 && (
				<ProductReviewFilter
					filters={reviewFilters}
					handleRemoveFilter={handleRemoveFilter}
					customClass="ProductReviewFilter--mobile"
				/>
			)}

			{isMobile
				? (
					globals && (globals.isProfessional || globals.isCustomer || (!globals.isLoggedIn && !globals.isStoreInSession))
						? (
							<div className={classNames({
								initialVisibility: customerPhotosState === 'loading',
								hidden: customerPhotosState === 'hidden',
								pdpFeedMobile: true,
								instagramFeed: true
							})}
							>
								<div className="pdSectionTitle">Customer Photos</div>
								<div id="pDPWidgetPixleeWidget">
									<PDPWidgetMobile
										productSku={window.shortSku}
										showShopNextPrevButtonToggleOn={pixleeWidgetShowNextPrevButtonPDPPageToggleOn}
										gaEvents={gaEvents}
										onLoadCallback={(hasItems) => {

											setCustomerPhotosState(hasItems ? 'show' : 'hidden');

										}}
									/>
								</div>
							</div>
						)
						: ''
				)
				: (
					<div className={classNames({
						initialVisibility: customerPhotosState === 'loading',
						hidden: customerPhotosState === 'hidden',
						pdSection: true
					})}
					>
						<div className="instagramFeed">
							<div className="pdSectionTitle">Customer Photos</div>
							<div id="pDPWidgetPixleeWidget">
								<PDPWidget
									productSku={window.shortSku}
									showShopNextPrevButtonToggleOn={pixleeWidgetShowNextPrevButtonPDPPageToggleOn}
									gaEvents={gaEvents}
									onLoadCallback={(hasItems) => {

										setCustomerPhotosState(hasItems ? 'show' : 'hidden');

									}}
								/>
							</div>
						</div>
					</div>
				)}

			{/* Sorting */}
			<div className="productReviews__searchFilterContainer">
				<ProductReviewsSearch showLabel={false} withSearchIcon placeholderText="Search reviews and questions" />
				{numOfReviews > 0 && (
					<div className="productReviews__sortSearchWrapper">
						<ProductReviewSort sortOptions={initialSortOptions} />
					</div>
				)}
				{/* Filters (desktop) */}
				{reviewFilters.length > 0 && (
					<ProductReviewFilter
						filters={reviewFilters}
						handleRemoveFilter={handleRemoveFilter}
						customClass="ProductReviewFilter--desktop"
					/>
				)}
			</div>
			{searchResults !== null && (
				<ProductSearchUGCResults
					instance="reviews"
					results={searchResults}
					isPendingResults={searchFetchStatus === 'pending'}
					isPendingShowMoreResults={showMoreResultsFectchStatus === 'pending'}
					productTitle={productData.productTitle}
					productCategory={productData.productCategory}
					handleClickShowMore={handleClickShowMore}
					totalResultsCount={totalResultsCount}
					totalResultsLeft={totalResultsLeft}
					searchTerm={searchTerm}
				/>
			)}
			<ProductReviewsList reviews={reviews} sortText={activeSortText} />
		</div>
	);

}

ProductReviews.propTypes = {
	productData: PropTypes.shape({
		productCategory: PropTypes.string,
		productShortSku: PropTypes.string,
		productTitle: PropTypes.string
	}),
	resetInitialState: PropTypes.bool
};

export default ProductReviews;
